import { z } from "zod";

import { untrack } from "~/tools/impulse";
import {
	type GetImpulseFormOutput,
	type GetImpulseFormOutputVerbose,
	ImpulseFormSwitch,
	ImpulseFormUnit,
} from "~/tools/impulse-form";

import type { DashboardPluginSpec } from "../../../common/dashboard-spec";
import { fromSync, toSync } from "../../widget-form/sync";
import type { FormProcessor } from "../_form-processor";

import { FormState as BarChartFormState } from "./bar-chart/form-state";
import { FormState as GaugeChartFormState } from "./gauge-chart/form-state";
import { FormState as StatChartFormState } from "./stat-chart/form-state";
import { FormState as TimeSeriesChartFormState } from "./time-series-chart/form-state";

const kindSchema = z.enum(["TimeSeriesChart", "BarChart", "GaugeChart", "StatChart"]).catch("TimeSeriesChart");

type Kind = z.infer<typeof kindSchema>;
type Spec = {
	[TKind in Kind]: Pick<Extract<DashboardPluginSpec, { kind: TKind }>, "kind" | "spec">;
}[Kind];

function specToInput({ kind, spec }: Spec) {
	return {
		active: kind,
		branches: {
			TimeSeriesChart: kind === "TimeSeriesChart" ? TimeSeriesChartFormState.specToInput(spec) : undefined,
			BarChart: kind === "BarChart" ? BarChartFormState.specToInput(spec) : undefined,
			GaugeChart: kind === "GaugeChart" ? GaugeChartFormState.specToInput(spec) : undefined,
			StatChart: kind === "StatChart" ? StatChartFormState.specToInput(spec) : undefined,
		},
	};
}

type FormState = ImpulseFormSwitch<
	ImpulseFormUnit<string, ReadonlyArray<string>, Spec["kind"]>,
	{
		TimeSeriesChart: TimeSeriesChartFormState;
		BarChart: BarChartFormState;
		GaugeChart: GaugeChartFormState;
		StatChart: StatChartFormState;
	}
>;

function outputToSpec({ kind, value }: GetImpulseFormOutput<FormState>): Spec {
	switch (kind) {
		case "TimeSeriesChart": {
			return {
				kind,
				spec: TimeSeriesChartFormState.outputToSpec(value),
			};
		}

		case "BarChart": {
			return {
				kind,
				spec: BarChartFormState.outputToSpec(value),
			};
		}

		case "GaugeChart": {
			return {
				kind,
				spec: GaugeChartFormState.outputToSpec(value),
			};
		}

		case "StatChart": {
			return {
				kind,
				spec: StatChartFormState.outputToSpec(value),
			};
		}
	}
}

function verboseOutputToSpec({ active, branches }: GetImpulseFormOutputVerbose<FormState>): Spec {
	switch (active) {
		case "BarChart": {
			return {
				kind: active,
				spec: BarChartFormState.verboseOutputToSpec(branches.BarChart),
			};
		}

		case "GaugeChart": {
			return {
				kind: active,
				spec: GaugeChartFormState.verboseOutputToSpec(branches.GaugeChart),
			};
		}

		case "StatChart": {
			return {
				kind: active,
				spec: StatChartFormState.verboseOutputToSpec(branches.StatChart),
			};
		}

		default: {
			return {
				kind: "TimeSeriesChart",
				spec: TimeSeriesChartFormState.verboseOutputToSpec(branches.TimeSeriesChart),
			};
		}
	}
}

type Input = ReturnType<typeof specToInput>;

function init(initial?: Input): FormState {
	return ImpulseFormSwitch(
		ImpulseFormUnit(initial?.active ?? ("TimeSeriesChart" satisfies Kind), {
			schema: kindSchema,
		}),
		{
			TimeSeriesChart: TimeSeriesChartFormState.init(initial?.branches.TimeSeriesChart),
			BarChart: BarChartFormState.init(initial?.branches.BarChart),
			GaugeChart: GaugeChartFormState.init(initial?.branches.GaugeChart),
			StatChart: StatChartFormState.init(initial?.branches.StatChart),
		},
	);
}

function setInput({ active, branches }: Input, state: FormState): void {
	state.active.setInput(active);

	if (branches.TimeSeriesChart != null) {
		TimeSeriesChartFormState.setInput(branches.TimeSeriesChart, state.branches.TimeSeriesChart);
	}

	if (branches.BarChart != null) {
		BarChartFormState.setInput(branches.BarChart, state.branches.BarChart);
	}

	if (branches.GaugeChart != null) {
		GaugeChartFormState.setInput(branches.GaugeChart, state.branches.GaugeChart);
	}

	if (branches.StatChart != null) {
		StatChartFormState.setInput(branches.StatChart, state.branches.StatChart);
	}
}

function sync(prevActive: Kind, state: FormState): void {
	untrack((scope) => {
		const synced = toSync(prevActive, state.getInput(scope).branches);

		if (synced != null) {
			fromSync(synced, state.branches);
		}
	});
}

const FormState = {
	init,
	sync,
	setInput,
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
} satisfies FormProcessor<Spec, FormState, Input> & {
	sync: typeof sync;
};

export { FormState };
